// Adaptador del super_admin para la consola de MÓDULOS de un negocio (lealify-base).
// Reusa GET /admin/tenants/:id + PUT /admin/tenants/:id/modules/:key (ya existían) y
// los nuevos GET/PUT /admin/tenants/:id/settings/:moduleKey + GET /admin/module-settings-schema.
import { baseApi } from './baseClient';

export interface ModuleRow {
  module_key: string;
  name: string;
  kind: 'core' | 'cross' | 'vertical' | string;
  vertical: string | null;
  is_core: number;        // 1 = no se puede apagar
  default_enabled: number;
  sort: number;
  entitled: number;       // 1 = el negocio tiene el módulo
  enabled: number;        // 1 = encendido
  applies?: number;       // 1 = aplica a la vertical del negocio (lo calcula el backend). 0 = no aplica a este giro.
}

export interface TenantInfo {
  id: number;
  name: string;
  vertical?: string | null;
  slug?: string | null;
  [k: string]: unknown;
}

export interface TenantModulesResponse {
  tenant: TenantInfo;
  modules: ModuleRow[];
}

// Un campo del formulario autogenerado (espejo del manifest de lib/moduleSettingsSchema.js).
export interface SettingsField {
  key: string;
  type: string;
  label: string;
  default?: unknown;
  help?: string;
  placeholder?: string;
  section?: string;
  order?: number;
  format?: 'int' | 'decimal' | 'percent' | 'money' | 'multiplier';
  min?: number;
  max?: number;
  step?: number;
  options?: string[];
  maxLength?: number;
  storageBool?: 'int';
  showIf?: Record<string, unknown[]>;
}

export interface ModuleSettingsResponse {
  moduleKey: string;
  storage: 'typed' | 'kv';
  label: string;
  schema: SettingsField[];
  settings: Record<string, unknown>;
  entitled: boolean;
}

// Metadatos del manifest por módulo (lo que devuelve /module-settings-schema).
export interface ModuleManifestMeta {
  label?: string;
  storage?: string;
  verticals?: string[];          // allowlist (ausente = todas)
  excludeVerticals?: string[];   // opt-out
  byVertical?: Record<string, unknown>;
  fields?: SettingsField[];
}

// ¿La config base del módulo aplica a esta vertical? Espejo de moduleAppliesToVertical en lealify-base.
export function moduleAppliesToVertical(meta: ModuleManifestMeta | undefined, vertical: string): boolean {
  if (!meta) return false;
  const v = vertical || 'lealtad';
  if (meta.byVertical && meta.byVertical[v]) return true;
  if (Array.isArray(meta.verticals) && !meta.verticals.includes(v)) return false;
  if (Array.isArray(meta.excludeVerticals) && meta.excludeVerticals.includes(v)) return false;
  return true;
}

// Un módulo se considera ACTIVO si es core o está habilitado + entitled.
export const isModuleActive = (m: ModuleRow): boolean =>
  m.is_core === 1 || (m.entitled === 1 && m.enabled === 1);

// ── POS ─────────────────────────────────────────────────────────────────────
// Módulo tal como lo devuelve el POS (su catálogo propio; nombres de campo distintos a
// ModuleRow porque es otro servicio — no forzamos la forma, se adapta al pintar).
export interface PosModuleRow {
  key: string;
  name: string;
  category: string | null;
  is_core: boolean;
  entitled: boolean;
  enabled: boolean;    // preferencia del dueño
  active: boolean;     // lo que de verdad ve la caja
}

export interface PosTenantInfo {
  id: number; name: string; slug: string; status: string; plan: string | null;
  branches: number; operators: number; devices: number; created_at: string;
}

export interface PosPanelResponse {
  linked: boolean;
  available?: boolean;      // false = el POS no respondió (o el puente no está configurado)
  error?: string;
  pos_tenant_id?: number;
  tenant?: PosTenantInfo;
  modules?: PosModuleRow[];
  // Salud de la integración de lealtad (pieza 5): calculada en lealify-base con sus propias
  // tablas, llega aunque el puente al POS esté caído. "Enlazado" ≠ "funcionando".
  health?: {
    status: 'sin_clave' | 'nunca' | 'ok' | 'quieto' | 'sin_actividad';
    keys: number; last_activity_at?: string | null;
    tokens_24h?: number; visits_24h?: number;
  };
}

export interface PosProvisionResponse {
  tenant_id: number; branch_id: number; slug: string;
  owner_email: string | null;
  owner_temp_password: string | null;   // solo en esta respuesta; después ya no se puede recuperar
}

export interface PosTenantOption {
  id: number; name: string; slug: string; status: string; branches: number;
  linked_to: { id: number; name: string; slug: string } | null;
}

export const modulesApi = {
  // Catálogo de módulos configurables + su manifest (para decidir aplicabilidad por vertical).
  getSettingsSchema: async (): Promise<Record<string, ModuleManifestMeta>> => {
    const r = await baseApi.get<{ modules: string[]; schema: Record<string, ModuleManifestMeta> }>('/admin/module-settings-schema');
    return r.schema || {};
  },

  getTenantModules: (tenantId: number | string): Promise<TenantModulesResponse> =>
    baseApi.get<TenantModulesResponse>(`/admin/tenants/${tenantId}`),

  // Toggle de módulo: ON → entitled+enabled=1; OFF → 0.
  setModule: (tenantId: number | string, moduleKey: string, on: boolean): Promise<{ ok: boolean }> =>
    baseApi.put<{ ok: boolean }>(`/admin/tenants/${tenantId}/modules/${moduleKey}`, {
      entitled: on ? 1 : 0,
      enabled: on ? 1 : 0,
    }),

  getModuleSettings: (tenantId: number | string, moduleKey: string): Promise<ModuleSettingsResponse> =>
    baseApi.get<ModuleSettingsResponse>(`/admin/tenants/${tenantId}/settings/${moduleKey}`),

  saveModuleSettings: (
    tenantId: number | string,
    moduleKey: string,
    settings: Record<string, unknown>,
  ): Promise<{ ok: boolean; settings: Record<string, unknown> }> =>
    baseApi.put(`/admin/tenants/${tenantId}/settings/${moduleKey}`, { settings }),

  // ── POS (consola única) ───────────────────────────────────────────────────
  // El POS es un servicio aparte con su propia base; base hace de puente. Por eso la
  // respuesta puede venir con available:false (POS reiniciando) sin que sea un error
  // de esta pantalla.
  getPos: (tenantId: number | string): Promise<PosPanelResponse> =>
    baseApi.get<PosPanelResponse>(`/admin/tenants/${tenantId}/pos`),

  // (F4) `setPosModule` se retiró: los módulos de caja son `pos_*` y se otorgan con `setModule`,
  // igual que los de cualquier otra vertical. La base del POS pasó a ser un espejo.

  // Le activa el POS a un negocio que ya es cliente: crea su espejo y lo deja enlazado.
  // Devuelve la contraseña temporal del dueño UNA vez (no se guarda en claro en ningún lado).
  provisionPos: (tenantId: number | string): Promise<PosProvisionResponse> =>
    baseApi.post<PosProvisionResponse>(`/admin/tenants/${tenantId}/pos/provision`, {}),

  // Clientes que existen en el POS (con su enlace actual), para casar a mano un gemelo previo.
  listPosTenants: (): Promise<{ available: boolean; error?: string; tenants: PosTenantOption[] }> =>
    baseApi.get(`/admin/pos/tenants`),

  linkPos: (tenantId: number | string, posTenantId: number | null): Promise<{ ok: boolean }> =>
    baseApi.put<{ ok: boolean }>(`/admin/tenants/${tenantId}/pos/link`, { pos_tenant_id: posTenantId }),
};

export default modulesApi;
